import Alpine from './alpine'

/**
 * The till.
 *
 * Written for somebody standing at a counter with a queue in front of them, which
 * dictates most of the decisions here: the search box keeps focus, a scanned barcode
 * adds its item without asking, and the change due is worked out as they type rather
 * than after they commit.
 */
Alpine.data('till', (config = {}) => ({
    searchUrl: config.searchUrl ?? '',
    currency: config.currency ?? 'GBP',
    taxRate: config.taxRate ?? 0,

    query: '',
    results: [],
    searching: false,
    timer: null,

    lines: [],
    tendered: '',
    method: config.method ?? 'cash',
    email: '',
    note: '',

    init() {
        this.$nextTick(() => this.focusSearch())
    },

    focusSearch() {
        this.$refs.search?.focus()
    },

    search() {
        window.clearTimeout(this.timer)

        if (this.query.trim() === '') {
            this.results = []

            return
        }

        // Short debounce: a scanner types a whole barcode in milliseconds, and waiting
        // longer than this makes the till feel broken to somebody in a hurry.
        this.timer = window.setTimeout(async () => {
            this.searching = true

            try {
                const response = await fetch(`${this.searchUrl}?q=${encodeURIComponent(this.query)}`, {
                    headers: { Accept: 'application/json' },
                })

                this.results = response.ok ? await response.json() : []

                // A scanner matched one item outright, so put it on the basket rather
                // than making somebody pick from a list of one.
                if (this.results.length === 1 && this.results[0].exact) {
                    this.add(this.results[0])
                }
            } catch (e) {
                this.results = []
            } finally {
                this.searching = false
            }
        }, 120)
    },

    add(item) {
        const existing = this.lines.find((line) => line.variant_id === item.variant_id)

        if (existing) {
            existing.quantity += 1
        } else {
            this.lines.push({
                variant_id: item.variant_id,
                title: item.title + (item.variant_title ? ` - ${item.variant_title}` : ''),
                sku: item.sku,
                price: item.price,
                quantity: 1,
            })
        }

        this.query = ''
        this.results = []
        this.focusSearch()
    },

    remove(index) {
        this.lines.splice(index, 1)
        this.focusSearch()
    },

    clear() {
        this.lines = []
        this.tendered = ''
        this.email = ''
        this.note = ''
        this.focusSearch()
    },

    get subtotal() {
        return this.lines.reduce((total, line) => total + Number(line.price || 0) * Number(line.quantity || 0), 0)
    },

    get tax() {
        return this.subtotal * Number(this.taxRate || 0)
    },

    get total() {
        return this.subtotal + this.tax
    },

    /** Only ever positive: "minus four pounds change" is not a thing. */
    get change() {
        const tendered = Number(this.tendered || 0)

        return tendered > this.total ? tendered - this.total : 0
    },

    get short() {
        const tendered = Number(this.tendered || 0)

        return tendered > 0 && tendered < this.total
    },

    money(amount) {
        return new Intl.NumberFormat(undefined, {
            style: 'currency',
            currency: this.currency,
        }).format(Number(amount || 0))
    },

    /** Common notes handed over, so nobody does mental arithmetic under pressure. */
    quickTender(amount) {
        this.tendered = String(amount)
    },

    get canSell() {
        return this.lines.length > 0 && ! this.short
    },
}))
